import React from 'react';
import { Card } from './ui/card';
import { Building2, Scale, Users, Trophy } from 'lucide-react';

export const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#7C2D12] mb-4">
              About Trademark Fashion Brand
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted partner in trademark registration and brand protection, providing comprehensive USPTO filing services to businesses nationwide.
            </p>
          </div>

          {/* Full Width Featured Image */}
          <div className="mb-12 relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl h-[300px] md:h-[400px]">
              <img 
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
                alt="Professional legal services"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#7C2D12]/80 to-transparent flex items-center">
                <div className="container mx-auto px-8">
                  <div className="max-w-2xl">
                    <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
                      Leading Experts in Trademark Registration
                    </h3>
                    <div className="flex gap-8">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-[#FED7AA] mb-1">15+</div>
                        <div className="text-white text-sm">Years Experience</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-[#FED7AA] mb-1">1000+</div>
                        <div className="text-white text-sm">Trademarks Filed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-[#FED7AA] mb-1">95%</div>
                        <div className="text-white text-sm">Success Rate</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full Width Text Content in Columns */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <p className="text-gray-700 leading-relaxed text-lg">
                  At Trademark Fashion Brand, we specialize exclusively in trademark registration services, dedicating 100% of our practice to helping businesses protect their most valuable asset—their brand identity. Unlike general legal firms that handle trademarks as a side service, our singular focus means we bring unparalleled expertise, efficiency, and up-to-date knowledge of USPTO procedures to every client engagement.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  With over 15 years of dedicated trademark experience and 1000+ successful filings across virtually every industry, we've built an impressive track record of navigating complex trademark scenarios. Our team includes USPTO-registered trademark attorneys, experienced paralegals, and trademark specialists who collectively bring decades of knowledge about federal registration law, office action responses, likelihood of confusion analysis, and enforcement strategies.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  We understand that every business is unique, which is why we don't believe in one-size-fits-all solutions. Whether you're a solo entrepreneur launching your first product line, a growing startup building brand recognition, or an established corporation managing an extensive trademark portfolio, we provide personalized guidance tailored to your specific industry, budget, and long-term business goals.
                </p>
              </div>
              <div className="space-y-5">
                <p className="text-gray-700 leading-relaxed text-lg">
                  Our comprehensive approach goes beyond simply filing paperwork. We take time to understand your business model, target market, expansion plans, and competitive landscape. This holistic perspective allows us to recommend the most strategic protection for your brand, including advice on mark selection, classification strategies, and timing considerations that can save you thousands of dollars and months of delays.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  What truly sets us apart is our commitment to transparency and communication. You'll never wonder about the status of your application—we provide regular updates, explain complex legal concepts in plain English, and remain accessible whenever questions arise. Our clients consistently praise our responsiveness, attention to detail, and ability to demystify the trademark process.
                </p>
                <Card className="p-6 bg-[#FFF7ED] border-2 border-[#F97316]">
                  <p className="text-[#7C2D12] font-semibold text-lg italic">
                    "Our singular focus on trademark registration means you get specialists, not generalists, working on your most valuable business asset."
                  </p>
                </Card>
              </div>
            </div>
          </div>

          {/* Core Values Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="p-6 bg-[#FFF7ED] border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300 hover:shadow-lg">
              <div className="bg-[#F97316] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Scale className="text-white" size={28} />
              </div>
              <h4 className="text-lg font-bold text-[#7C2D12] mb-2">Legal Expertise</h4>
              <p className="text-gray-600 text-sm">
                Experienced trademark attorneys with deep USPTO knowledge
              </p>
            </Card>

            <Card className="p-6 bg-[#FFF7ED] border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300 hover:shadow-lg">
              <div className="bg-[#F97316] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Trophy className="text-white" size={28} />
              </div>
              <h4 className="text-lg font-bold text-[#7C2D12] mb-2">Proven Success</h4>
              <p className="text-gray-600 text-sm">
                95% success rate in trademark application approvals
              </p>
            </Card>

            <Card className="p-6 bg-[#FFF7ED] border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300 hover:shadow-lg">
              <div className="bg-[#F97316] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-white" size={28} />
              </div>
              <h4 className="text-lg font-bold text-[#7C2D12] mb-2">Client-Focused</h4>
              <p className="text-gray-600 text-sm">
                Personalized service with dedicated case managers
              </p>
            </Card>

            <Card className="p-6 bg-[#FFF7ED] border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300 hover:shadow-lg">
              <div className="bg-[#F97316] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="text-white" size={28} />
              </div>
              <h4 className="text-lg font-bold text-[#7C2D12] mb-2">Nationwide Service</h4>
              <p className="text-gray-600 text-sm">
                Protecting brands across all 50 states and industries
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
